import { checkArguments, elementRenderer } from "./base.js";

/**
 * Define a new element.
 *
 * It parses the all function arguments to extract the arguments to pass to the
 * `fn` function. The extracted arguments are:
 *   - {object} options - The options passed to the element.
 *   - {Renderable[]} children - All the children embedded by the element.
 *
 * This makes it compatible with HTMLCanvas.
 *
 *
 * Since `class` is a JS keyword, it can't really be used everywhere. It can be
 * used as an object key, but not as a variable name.
 *
 * ```js
 * let foo = { class: 42 }; // Valid
 * let { class } = computeClass(); // Invalid
 * ```
 *
 * When invoked, the `fn` function must return a literal object of the
 * following form:
 *  - tag: the name of a tagBrush that will be used to render
 *      the top level DOMElement of the element. This can be a function
 *      as well, that will be invoked with
 *        - {HtmlCanvas} html - HTML canvas used to render the element
 *        - {object} options - Tag attributes
 *        - {Renderable[]} children - List of children to append to the tag
 *        - {string} defaultTag - Tag brush used by default
 *  - options: options to pass to the top level DOMElement of the element
 *  - children: children of the top level DOMElement of the element
 *
 * This allows to intercept the creation of the root element, and use the `as`
 * parameter if provided.
 * It will also make sure that the `class` parameter is injected as well as the
 * element-specific class name.
 *
 * @param {string} name - Name of the element to define. This string will also
 *   be used to generate a CSS class specific to this element
 * @param {function} fn - Function invoke to render the element. This method is
 *   invoked with some options as a literal object, and a collection of
 *   Renderables.
 * @return {Renderable}
 */
export default function defineElement(name, fn) {
	return (...args) => {
		const { options, children } = checkArguments(...args);
		return elementRenderer({ options, children, name, fn });
	};
}
